import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../slices/types';
import { setViewMode } from '../slices/users/reducer';
import { 
  fetchAdminUsers, 
  fetchUserStats,
  createUser, 
  updateUser, 
  singlePointsOperation, 
  bulkPointsOperation, 
  assignApp,
  blockUser,
  changeLoginType,
  type User,
  type CreateUserData,
  type UpdateUserData,
  type PointsOperation,
  type AppAssignment,
  type BlockUserData,
  type ChangeLoginTypeData
} from '../slices/users/adminUsersSlice';
import { getRoleFromAccessToken, getAppIdFromAccessToken, getUserIdFromAccessToken, getLoginTypeFromAccessToken } from '../helpers/jwt';
import { registerUser } from '../slices/registration/registerSlice';
import { 
  Grid, 
  List, 
  Search, 
  Filter, 
  MoreHorizontal, 
  UserPlus, 
  Edit, 
  Coins, 
  Smartphone, 
  Activity,
  RefreshCw,
  Calendar,
  Users as UsersIcon,
  UserCheck,
  TrendingUp,
  BarChart3,
  Ban,
  Unlock,
  Key,
} from 'lucide-react';
import { fetchAgentUsersFromReferrals } from '../slices/agentUsers/agentUsers';

const Users: React.FC = () => {
    const dispatch = useAppDispatch();
    const { users: adminUsers, loading: adminLoading, error: adminError, stats, selectedUsers, currentOperation, totalCount, filteredCount } = useAppSelector((state) => state.AdminUsers);
    const { viewMode } = useAppSelector((state) => state.Users);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [joinedSort, setJoinedSort] = useState<'newest' | 'oldest'>('newest');
    const [showStats, setShowStats] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState('Last 30 days');
    const [hasInitialized, setHasInitialized] = useState(false);
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showPointsModal, setShowPointsModal] = useState(false);
    const [showBulkPointsModal, setShowBulkPointsModal] = useState(false);
    const [showAppAssignmentModal, setShowAppAssignmentModal] = useState(false);
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [showLoginTypeModal, setShowLoginTypeModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const role = getRoleFromAccessToken() || '';
    const isAgentInternal = role.toLowerCase() === 'agent (internal)';
    
    // Form states
    const [createForm, setCreateForm] = useState<CreateUserData>({
        mobile_number: '',
        password: '',
        first_name: '',
        last_name: '',
        app_id: '90d8de53-2ac6-4e91-b521-010da104aca9',
        initial_points: 0,
        is_staff: false,
        is_active: true,
        role_id: 3, // Default to "User" role (role_id: 3)
    });
    const [referralCode, setReferralCode] = useState('');
    const [mobileNumberError, setMobileNumberError] = useState('');
    
    const [updateForm, setUpdateForm] = useState<UpdateUserData>({
        user_id: 0,
        first_name: '',
        last_name: '',
        email: '',
        points: 0,
        is_active: true,
        is_staff: false,
    });
    
    const [blockForm, setBlockForm] = useState<BlockUserData>({
        operation: 'block' as 'block' | 'unblock',
        user_id: 0,
    });
    
    const [loginTypeForm, setLoginTypeForm] = useState<ChangeLoginTypeData>({
        operation: 'change_login_type',
        user_id: 0,
        new_login_type: 'mpin',
        new_credential: '',
    });
    
    const [pointsForm, setPointsForm] = useState<PointsOperation>({
        user_id: 0,
        action: 'add',
        amount: 0,
        comment: '',
    });
    
    const [bulkPointsForm, setBulkPointsForm] = useState({
        action: 'add' as 'add' | 'deduct',
        amount: 0,
        user_ids: [] as number[],
        mobile_numbers: [] as string[],
    });
    
    const [appAssignmentForm, setAppAssignmentForm] = useState<AppAssignment>({
        app_id: '90d8de53-2ac6-4e91-b521-010da104aca9',
        user_ids: [],
        remove_app: false,
    });

    // Debug: Log the entire Redux state
    const fullState = useAppSelector((state) => state);
    console.log('Full Redux state:', fullState);
    console.log('AdminUsers slice state:', fullState.AdminUsers);

    useEffect(() => {
        if (!hasInitialized) {
            if (isAgentInternal) {
                dispatch(fetchAgentUsersFromReferrals());
            }
            setHasInitialized(true);
        }
    }, [dispatch, hasInitialized, isAgentInternal]);

    useEffect(() => {
        if (!isAgentInternal) {
            dispatch(fetchUserStats(selectedPeriod));
        }
    }, [dispatch, selectedPeriod, isAgentInternal]);

    const agentUsers = useAppSelector((state) => state.AgentUsers.users as any[]);
    const agentUsersLoading = useAppSelector((state) => state.AgentUsers.loading);
    const agentUsersError = useAppSelector((state) => state.AgentUsers.error);
    useEffect(() => {
        if (isAgentInternal) {
            // map agentUsers into the User view model used by this screen
            const mapped = (agentUsers || []).map((u: any) => ({
                id: u.user_id || u.id,
                full_name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                first_name: u.first_name || '',
                last_name: u.last_name || '',
                mobile_number: u.mobile_number || '',
                email: u.email || '',
                is_active: u.status !== 'inactive' && u.status !== 'blocked',
                is_staff: false,
                login_type: u.login_type || '',
                created_at: u.created_at || u.joined_date || '',
                points: u.points ?? 0,
                status_display: (u.status === 'inactive' || u.status === 'blocked') ? 'Inactive' : 'Active',
              }));
            setFilteredUsers(mapped as any);
        } else {
            if (adminUsers.length > 0) {
                setFilteredUsers(adminUsers);
            }
        }
    }, [isAgentInternal, agentUsers, adminUsers]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        if (!isAgentInternal) {
            dispatch(fetchAdminUsers({
                page: currentPage,
                pageSize: 50,
                search: debouncedSearch,
                sort: joinedSort,
            }));
        }
    }, [dispatch, isAgentInternal, currentPage, debouncedSearch, joinedSort]);

    useEffect(() => {
        if (!isAgentInternal) {
            setFilteredUsers(adminUsers);
        }
    }, [adminUsers, isAgentInternal]);

    // Function to check if mobile number already exists
    const checkMobileNumberExists = (mobileNumber: string): boolean => {
        if (!mobileNumber || mobileNumber.length !== 10) return false;
        
        // Normalize the input mobile number (remove all non-digits, take last 10 digits)
        const normalizedInput = mobileNumber.replace(/\D/g, '').slice(-10);
        
        if (isAgentInternal) {
            // Check against agentUsers directly (source of truth)
            if (!agentUsers || agentUsers.length === 0) {
                console.log('checkMobileNumberExists: No agentUsers available');
                return false;
            }
            
            const exists = agentUsers.some((u: any) => {
                const existingMobile = u.mobile_number || '';
                // Normalize existing mobile (remove all non-digits, take last 10 digits)
                const normalizedExisting = existingMobile.replace(/\D/g, '').slice(-10);
                // Check if last 10 digits match
                const matches = normalizedExisting === normalizedInput && normalizedInput.length === 10;
                if (matches) {
                    console.log('checkMobileNumberExists: Found duplicate', {
                        input: mobileNumber,
                        normalizedInput,
                        existing: existingMobile,
                        normalizedExisting
                    });
                }
                return matches;
            });
            
            console.log('checkMobileNumberExists: Result', { mobileNumber, normalizedInput, exists, agentUsersCount: agentUsers.length });
            return exists;
        } else {
            // Admin results are paginated, so the loaded users are not
            // a complete source for mobile-number uniqueness.
            // The admin create-user API validates against the database.
            return false;
        }
    };

    // Handle mobile number change with validation (using useEffect for debounce)
    const handleMobileNumberChange = (value: string) => {
        // Remove non-digit characters and limit to 10 digits
        const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
        setCreateForm(prev => ({ ...prev, mobile_number: cleanedValue }));
        
        // Clear error when user starts typing
        if (mobileNumberError) {
            setMobileNumberError('');
        }
    };

    // Debounced validation for mobile number
    useEffect(() => {
        console.log('Mobile validation useEffect triggered', {
            mobileNumber: createForm.mobile_number,
            length: createForm.mobile_number.length,
            isAgentInternal,
            agentUsersCount: agentUsers?.length || 0
        });
        
        if (createForm.mobile_number.length === 10 && isAgentInternal) {
            const timeoutId = setTimeout(() => {
                console.log('Running mobile number validation check...');
                const exists = checkMobileNumberExists(createForm.mobile_number);
                console.log('Validation result:', exists);
                if (exists) {
                    setMobileNumberError('User with this mobile number already exists.');
                } else {
                    setMobileNumberError('');
                }
            }, 500); // 500ms debounce
            
            return () => clearTimeout(timeoutId);
        } else if (createForm.mobile_number.length < 10 && mobileNumberError) {
            // Clear error if not 10 digits yet
            setMobileNumberError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createForm.mobile_number, isAgentInternal, agentUsers, adminUsers]);

    const handleViewModeChange = (mode: 'list' | 'grid') => {
        console.log('Changing view mode to:', mode);
        dispatch(setViewMode(mode));
    };

    // Handle period change
    const handlePeriodChange = (period: string) => {
        setSelectedPeriod(period);
        dispatch(fetchUserStats(period));
    };

    // Handle user actions
    const handleEditUser = (user: User) => {
        setSelectedUser(user);
        console.log('Editing user:', user);
        console.log('User points (raw):', user.points, 'Type:', typeof user.points);
        
        const pointsValue = typeof user.points === 'string' ? parseFloat(user.points) : (user.points || 0);
        console.log('Converted points:', pointsValue);
        
        setUpdateForm({
            user_id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email || '',
            points: pointsValue,
            is_active: user.is_active,
            is_staff: user.is_staff || false,
        });
        setShowUpdateModal(true);
    };

    const handlePointsOperation = (user: User) => {
        setSelectedUser(user);
        setPointsForm({
            user_id: user.id,
            action: 'add',
            amount: 0,
        });
        setShowPointsModal(true);
    };

    const handleAppAssignment = (user: User) => {
        setSelectedUser(user);
        setAppAssignmentForm(prev => ({
            ...prev,
            user_ids: [user.id],
        }));
        setShowAppAssignmentModal(true);
    };

    const handleBlockUser = (user: User) => {
        setSelectedUser(user);
        const isBlocked = !user.is_active || user.status_display === 'Inactive';
        setBlockForm({
            operation: (isBlocked ? 'unblock' : 'block') as 'block' | 'unblock',
            user_id: user.id,
        });
        setShowBlockModal(true);
    };

    const handleChangeLoginType = (user: User) => {
        setSelectedUser(user);
        setLoginTypeForm({
            operation: 'change_login_type',
            user_id: user.id,
            new_login_type: user.login_type === 'mpin' ? 'password' : 'mpin',
            new_credential: '',
        });
        setShowLoginTypeModal(true);
    };

    // Form submission handlers
    const handleCreateUser = async () => {
        // Validate mobile number before API call
        if (createForm.mobile_number.length === 10) {
            if (checkMobileNumberExists(createForm.mobile_number)) {
                setMobileNumberError('User with this mobile number already exists.');
                return;
            }
        }
        
        // Clear any existing error
        setMobileNumberError('');
        
        try {
            if (isAgentInternal) {
                const appId = getAppIdFromAccessToken() || '';
                const onBehalfBy = getUserIdFromAccessToken();
                const grantType = getLoginTypeFromAccessToken() || 'password';
                const pwd = createForm.password || Math.random().toString().slice(2, 8);

                if (!appId || !onBehalfBy) {
                    throw new Error('Missing app_id or on_behalf_by from token');
                }

                await dispatch(registerUser({
                    mobile_number: createForm.mobile_number,
                    first_name: createForm.first_name,
                    last_name: createForm.last_name || '',
                    grant_type: grantType,
                    password: pwd,
                    app_id: appId,
                    on_behalf_by: onBehalfBy,
                })).unwrap();
            } else {
            await dispatch(createUser(createForm)).unwrap();
            }
            setShowCreateModal(false);
            setCreateForm({
                mobile_number: '',
                password: '',
                first_name: '',
                last_name: '',
                app_id: '90d8de53-2ac6-4e91-b521-010da104aca9',
                initial_points: 0,
                is_staff: false,
                is_active: true,
                role_id: 3, // Default to "User" role (role_id: 3)
            });
            setReferralCode('');
            setMobileNumberError('');
            dispatch(fetchAdminUsers({
                    page: currentPage,
                    pageSize: 50,
                    search: debouncedSearch,
                    sort: joinedSort,
                }));
        } catch (error: any) {
            console.error('Error creating user:', error);
            console.error('Error details:', {
                error,
                payload: error?.payload,
                response: error?.response,
                responseData: error?.response?.data
            });
            
            // Handle backend error for mobile number
            // When using .unwrap() with rejectWithValue, the error payload is available
            let mobileError = null;
            
            // Check error.payload (from rejectWithValue - this is the full response.data now)
            if (error?.payload?.mobile_number) {
                mobileError = Array.isArray(error.payload.mobile_number) 
                    ? error.payload.mobile_number[0] 
                    : error.payload.mobile_number;
            }
            // Check error.response.data (direct axios error - fallback)
            else if (error?.response?.data?.mobile_number) {
                mobileError = Array.isArray(error.response.data.mobile_number) 
                    ? error.response.data.mobile_number[0] 
                    : error.response.data.mobile_number;
            }
            // Check if error message contains mobile number info
            else if (error?.message && (error.message.toLowerCase().includes('mobile') || error.message.toLowerCase().includes('phone'))) {
                mobileError = error.message;
            }
            
            if (mobileError) {
                console.log('Setting mobile number error:', mobileError);
                setMobileNumberError(mobileError);
            } else {
                // Fallback: show generic error if mobile number validation fails
                console.log('No specific mobile error found, using fallback');
                setMobileNumberError('User with this mobile number already exists.');
            }
            
            // Don't close the modal on error - keep it open to show the error
        }
    };

    const handleUpdateUser = async () => {
        try {
            await dispatch(updateUser(updateForm)).unwrap();
            setShowUpdateModal(false);
            dispatch(fetchAdminUsers({
                    page: currentPage,
                    pageSize: 50,
                    search: debouncedSearch,
                    sort: joinedSort,
                }));
        } catch (error) {
            console.error('Error updating user:', error);
        }
    };

    const handlePointsSubmit = async () => {
        try {
            await dispatch(singlePointsOperation(pointsForm)).unwrap();
            setShowPointsModal(false);
            dispatch(fetchAdminUsers({
                    page: currentPage,
                    pageSize: 50,
                    search: debouncedSearch,
                    sort: joinedSort,
                }));
        } catch (error) {
            console.error('Error updating points:', error);
        }
    };

    const handleBulkPointsSubmit = async () => {
        try {
            await dispatch(bulkPointsOperation(bulkPointsForm)).unwrap();
            setShowBulkPointsModal(false);
            dispatch(fetchAdminUsers({
                    page: currentPage,
                    pageSize: 50,
                    search: debouncedSearch,
                    sort: joinedSort,
                }));
        } catch (error) {
            console.error('Error updating bulk points:', error);
        }
    };

    const handleAppAssignmentSubmit = async () => {
        try {
            await dispatch(assignApp(appAssignmentForm)).unwrap();
            setShowAppAssignmentModal(false);
            dispatch(fetchAdminUsers({
                    page: currentPage,
                    pageSize: 50,
                    search: debouncedSearch,
                    sort: joinedSort,
                }));
        } catch (error) {
            console.error('Error assigning app:', error);
        }
    };

    const handleBlockSubmit = async () => {
        try {
            await dispatch(blockUser(blockForm)).unwrap();
            setShowBlockModal(false);
            dispatch(fetchAdminUsers({
                    page: currentPage,
                    pageSize: 50,
                    search: debouncedSearch,
                    sort: joinedSort,
                }));
        } catch (error) {
            console.error('Error updating user status:', error);
        }
    };

    const handleLoginTypeSubmit = async () => {
        try {
            await dispatch(changeLoginType(loginTypeForm)).unwrap();
            setShowLoginTypeModal(false);
            dispatch(fetchAdminUsers({
                    page: currentPage,
                    pageSize: 50,
                    search: debouncedSearch,
                    sort: joinedSort,
                }));
        } catch (error) {
            console.error('Error changing login type:', error);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Debug logging
    console.log('Users state:', { adminUsers: adminUsers.length, adminLoading, adminError, viewMode, totalCount, filteredUsers: filteredUsers.length });

    // Determine loading and error states based on user role
    const isLoading = isAgentInternal ? agentUsersLoading : adminLoading;
    const error = isAgentInternal ? agentUsersError : adminError;
    const hasUsers = isAgentInternal ? (agentUsers?.length > 0 || filteredUsers.length > 0) : adminUsers.length > 0;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading users...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="text-red-500 text-xl mb-4">Error: {error}</div>
                    <button 
                        onClick={() => {
                            if (isAgentInternal) { 
                                dispatch(fetchAgentUsersFromReferrals());
                            } else {
                                dispatch(fetchAdminUsers({
                    page: currentPage,
                    pageSize: 50,
                    search: debouncedSearch,
                    sort: joinedSort,
                }));
                            }
                        }}
                        className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Users Management</h1>
                    <p className="text-gray-600 dark:text-gray-400">Manage and view all users in the system</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowStats(!showStats)}
                        className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            showStats 
                                ? 'text-white bg-blue-600 hover:bg-blue-700' 
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        <Activity className="w-4 h-4" />
                        {showStats ? 'Hide Stats' : 'Show Stats'}
                    </button>
                    <button
                        onClick={() => {
                            setShowCreateModal(true);
                            setMobileNumberError('');
                            if (isAgentInternal) {
                                setReferralCode('');
                                setCreateForm(prev => ({
                                    ...prev,
                                    // safe defaults when fields are hidden
                                    password: prev.password || Math.random().toString().slice(2, 8),
                                    last_name: '',
                                    initial_points: 0,
                                    is_staff: false,
                                    role_id: 3,
                                }));
                            }
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <UserPlus className="w-4 h-4" />
                        Create User
                    </button>
                    <button
                        onClick={() => {
                            if (isAgentInternal) {
                                dispatch(fetchAgentUsersFromReferrals());
                            } else {
                                dispatch(fetchAdminUsers({
                    page: currentPage,
                    pageSize: 50,
                    search: debouncedSearch,
                    sort: joinedSort,
                }));
                                dispatch(fetchUserStats(selectedPeriod));
                            }
                        }}
                        disabled={adminLoading}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${adminLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Stats Dashboard */}
            {showStats && stats && (
                <div className="mb-6">
                    <div className="space-y-6">
                        {/* Period Selector */}
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">User Statistics</h2>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-500" />
                                <select
                                    value={selectedPeriod}
                                    onChange={(e) => handlePeriodChange(e.target.value)}
                                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                >
                                    <option value="Last 7 days">Last 7 days</option>
                                    <option value="Last 30 days">Last 30 days</option>
                                    <option value="Last 90 days">Last 90 days</option>
                                    <option value="Last year">Last year</option>
                                </select>
                            </div>
                        </div>

                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* Total Users */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <UsersIcon size={32} className="text-blue-600" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Users</p>
                                        <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total_users}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Active Users */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <UserCheck className="h-8 w-8 text-green-600" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Users</p>
                                        <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.active_users}</p>
                                    </div>
                                </div>
                            </div>

                            {/* New Users Today */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <UserPlus className="h-8 w-8 text-purple-600" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">MPIN Users</p>
                                        <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.login_type_distribution.find(
                                            (login) => login.login_type === 'mpin',
                                        )?.count ?? 0}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Staff Users */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <TrendingUp className="h-8 w-8 text-orange-600" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Password Users</p>
                                        <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.login_type_distribution.find(
                                            (login) => login.login_type === 'password',
                                        )?.count ?? 0}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Stats */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Points Statistics */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                <div className="flex items-center mb-4">
                                    <Coins className="h-6 w-6 text-yellow-600 mr-2" />
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Points Statistics</h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-500 dark:text-gray-400">Total Points</span>
                                        <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {stats.total_points.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-500 dark:text-gray-400">Average Points</span>
                                        <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {stats.average_points.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Login Type Distribution */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                <div className="flex items-center mb-4">
                                    <BarChart3 className="h-6 w-6 text-purple-600 mr-2" />
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Login Types</h3>
                                </div>
                                <div className="space-y-3">
                                    {stats.login_type_distribution.map((login, index) => (
                                        <div key={index} className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <div className={`w-3 h-3 rounded-full mr-3 ${
                                                    login.login_type === 'password' ? 'bg-green-500' :
                                                    login.login_type === 'mpin' ? 'bg-blue-500' :
                                                    'bg-gray-500'
                                                }`}></div>
                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                    {login.login_type || 'Unknown'}
                                                </span>
                                            </div>
                                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                                {login.count} users
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Fallback Stats (when stats not available) */}
            {!showStats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-sm text-gray-600">Total Users</div>
                        <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-sm text-gray-600">Active Users</div>
                        <div className="text-2xl font-bold text-green-600">
                            {adminUsers.length > 0 ? adminUsers.filter((u) => u.is_active).length : 0}
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-sm text-gray-600">MPIN Users</div>
                        <div className="text-2xl font-bold text-blue-600">
                            {adminUsers.length > 0 ? adminUsers.filter((u) => u.login_type === 'mpin').length : 0}
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-sm text-gray-600">Password Users</div>
                        <div className="text-2xl font-bold text-purple-600">
                            {adminUsers.length > 0 ? adminUsers.filter((u) => u.login_type === 'password').length : 0}
                        </div>
                    </div>
                </div>
            )}

            {/* Controls */}
            <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                            <Filter className="w-5 h-5" />
                        </button>
                        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                            <button
                                onClick={() => handleViewModeChange('list')}
                                className={`p-2 ${viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                            >
                                <List className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => handleViewModeChange('grid')}
                                className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Debug Info */}
            {/* <div className="mb-4 p-3 bg-gray-100 rounded text-sm">
                <p>View Mode: <strong>{viewMode}</strong> | Users: <strong>{users.length}</strong> | Filtered: <strong>{filteredUsers.length}</strong></p>
                <p>Total Count: <strong>{totalCount}</strong> | Loading: <strong>{loading ? 'Yes' : 'No'}</strong> | Error: <strong>{error || 'None'}</strong></p>
                <button 
                    onClick={() => dispatch(testAction())}
                    className="mt-2 bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600"
                >
                    Test Redux Store
                </button>
            </div> */}

            {/* Content */}
            {filteredUsers.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                    <div className="text-gray-500 text-xl mb-4">No users found</div>
                    <p className="text-gray-400">Get started by creating a new user.</p>
                </div>
            ) : viewMode === 'list' ? (
                <ListView 
                    users={[...filteredUsers].sort((a, b) => {
                        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
                        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;

                        return joinedSort === 'newest'
                            ? bTime - aTime
                            : aTime - bTime;
                    })}
                    joinedSort={joinedSort}
                    onToggleJoinedSort={() =>
                        setJoinedSort(current =>
                            current === 'newest' ? 'oldest' : 'newest'
                        )
                    } 
                    formatDate={formatDate}
                    onEditUser={handleEditUser}
                    onPointsOperation={handlePointsOperation}
                    onAppAssignment={handleAppAssignment}
                    onBlockUser={handleBlockUser}
                    onChangeLoginType={handleChangeLoginType}
                />
            ) : (
                <GridView 
                    users={filteredUsers} 
                    formatDate={formatDate}
                    onEditUser={handleEditUser}
                    onPointsOperation={handlePointsOperation}
                    onAppAssignment={handleAppAssignment}
                    onBlockUser={handleBlockUser}
                    onChangeLoginType={handleChangeLoginType}
                />
            )}

            {!isAgentInternal && (
                <div className="flex items-center justify-between gap-4 mt-6 mb-6 p-4 bg-white rounded-lg border">
                    <div className="text-sm text-gray-600">
                        Showing {adminUsers.length} of {filteredCount} matching users
                        {' '}| Page {currentPage} of {Math.max(1, Math.ceil(filteredCount / 50))}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            disabled={currentPage <= 1 || adminLoading}
                            onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                            className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            disabled={currentPage >= Math.ceil(filteredCount / 50) || adminLoading}
                            onClick={() => setCurrentPage(page => page + 1)}
                            className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Modals */}
            {/* Create User Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Create New User</h3>
                        {isAgentInternal ? (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Mobile Number</label>
                                    <input
                                        type="text"
                                        value={createForm.mobile_number}
                                        onChange={(e) => handleMobileNumberChange(e.target.value)}
                                        onBlur={() => {
                                            if (createForm.mobile_number.length === 10) {
                                                if (checkMobileNumberExists(createForm.mobile_number)) {
                                                    setMobileNumberError('User with this mobile number already exists.');
                                                } else {
                                                    setMobileNumberError('');
                                                }
                                            }
                                        }}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                                            mobileNumberError 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-gray-300 focus:ring-blue-500'
                                        }`}
                                        placeholder="Enter 10-digit mobile number"
                                        maxLength={10}
                                    />
                                    {mobileNumberError && (
                                        <p className="mt-1 text-sm text-red-600">{mobileNumberError}</p>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">First Name</label>
                                        <input
                                            type="text"
                                            value={createForm.first_name}
                                            onChange={(e) => setCreateForm(prev => ({ ...prev, first_name: e.target.value }))}
                                            className="w-full px-3 py-2 border rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Last Name</label>
                                        <input
                                            type="text"
                                            value={createForm.last_name}
                                            onChange={(e) => setCreateForm(prev => ({ ...prev, last_name: e.target.value }))}
                                            className="w-full px-3 py-2 border rounded-md"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Password</label>
                                    <input
                                        type="password"
                                        value={createForm.password}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                                        className="w-full px-3 py-2 border rounded-md"
                                        placeholder="Enter password"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Referral Code</label>
                                    <input
                                        type="text"
                                        value={referralCode}
                                        onChange={(e) => setReferralCode(e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md"
                                        placeholder="Enter referral code"
                                    />
                                </div>
                            </div>
                        ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Mobile Number</label>
                                <input
                                    type="text"
                                    value={createForm.mobile_number}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, mobile_number: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Password</label>
                                <input
                                    type="password"
                                    value={createForm.password}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">First Name</label>
                                    <input
                                        type="text"
                                        value={createForm.first_name}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, first_name: e.target.value }))}
                                        className="w-full px-3 py-2 border rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Last Name</label>
                                    <input
                                        type="text"
                                        value={createForm.last_name}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, last_name: e.target.value }))}
                                        className="w-full px-3 py-2 border rounded-md"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Initial Points</label>
                                <input
                                    type="number"
                                    value={createForm.initial_points}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, initial_points: Number(e.target.value) }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={createForm.is_staff}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, is_staff: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Staff User
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={createForm.is_active}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Active
                                </label>
                            </div>
                        </div>
                        )}
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleCreateUser}
                                disabled={!!mobileNumberError || createForm.mobile_number.length !== 10}
                                className={`flex-1 py-2 px-4 rounded-md ${
                                    mobileNumberError || createForm.mobile_number.length !== 10
                                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                Create User
                            </button>
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setMobileNumberError('');
                                }}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Update User Modal */}
            {showUpdateModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Update User</h3>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">First Name</label>
                                    <input
                                        type="text"
                                        value={updateForm.first_name}
                                        onChange={(e) => setUpdateForm(prev => ({ ...prev, first_name: e.target.value }))}
                                        className="w-full px-3 py-2 border rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Last Name</label>
                                    <input
                                        type="text"
                                        value={updateForm.last_name}
                                        onChange={(e) => setUpdateForm(prev => ({ ...prev, last_name: e.target.value }))}
                                        className="w-full px-3 py-2 border rounded-md"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Email</label>
                                <input
                                    type="email"
                                    value={updateForm.email}
                                    onChange={(e) => setUpdateForm(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Points</label>
                                <input
                                    type="number"
                                    value={updateForm.points || 0}
                                    onChange={(e) => setUpdateForm(prev => ({ ...prev, points: Number(e.target.value) }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                    placeholder="Enter points"
                                />
                                {/* Debug: {updateForm.points} */}
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={updateForm.is_staff}
                                        onChange={(e) => setUpdateForm(prev => ({ ...prev, is_staff: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Staff User
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={updateForm.is_active}
                                        onChange={(e) => setUpdateForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Active
                                </label>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleUpdateUser}
                                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                            >
                                Update User
                            </button>
                            <button
                                onClick={() => setShowUpdateModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Points Operation Modal */}
            {showPointsModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Manage Points - {selectedUser.full_name}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Action</label>
                                <select
                                    value={pointsForm.action}
                                    onChange={(e) => setPointsForm(prev => ({ ...prev, action: e.target.value as 'add' | 'deduct' }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                >
                                    <option value="add">Add Points</option>
                                    <option value="deduct">Deduct Points</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Amount</label>
                                <input
                                    type="number"
                                    value={pointsForm.amount || ''}
                                    onChange={(e) => setPointsForm(prev => ({ ...prev, amount: Number(e.target.value) || 0 }))}
                                    placeholder="Enter amount"
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Remarks
                                </label>
                                <textarea
                                    value={pointsForm.comment || ''}
                                    onChange={(e) =>
                                        setPointsForm(prev => ({
                                            ...prev,
                                            comment: e.target.value,
                                        }))
                                    }
                                    placeholder={
                                        pointsForm.action === 'deduct'
                                            ? 'Enter reason for deduction'
                                            : 'Enter remark for addition'
                                    }
                                    rows={3}
                                    maxLength={250}
                                    className="w-full px-3 py-2 border rounded-md resize-none"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Optional - this remark will appear in the wallet ledger/report.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handlePointsSubmit}
                                className="flex-1 bg-yellow-600 text-white py-2 px-4 rounded-md hover:bg-yellow-700"
                            >
                                Update Points
                            </button>
                            <button
                                onClick={() => setShowPointsModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* App Assignment Modal */}
            {showAppAssignmentModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Assign App - {selectedUser.full_name}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">App ID</label>
                                <input
                                    type="text"
                                    value={appAssignmentForm.app_id}
                                    onChange={(e) => setAppAssignmentForm(prev => ({ ...prev, app_id: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={appAssignmentForm.remove_app}
                                    onChange={(e) => setAppAssignmentForm(prev => ({ ...prev, remove_app: e.target.checked }))}
                                    className="mr-2"
                                />
                                <label className="text-sm font-medium">Remove App Assignment</label>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleAppAssignmentSubmit}
                                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700"
                            >
                                {appAssignmentForm.remove_app ? 'Remove App' : 'Assign App'}
                            </button>
                            <button
                                onClick={() => setShowAppAssignmentModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Block/Unblock User Modal */}
            {showBlockModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md transform transition-all">
                        {/* Header */}
                        {(() => {
                            const isBlocked = !selectedUser.is_active || selectedUser.status_display === 'Inactive';
                            return (
                                <div className={`px-6 py-4 rounded-t-xl ${
                                    isBlocked 
                                        ? 'bg-green-50 border-b border-green-200' 
                                        : 'bg-red-50 border-b border-red-200'
                                }`}>
                                    <div className="flex items-center">
                                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                                            isBlocked 
                                                ? 'bg-green-100' 
                                                : 'bg-red-100'
                                        }`}>
                                            {isBlocked ? (
                                                <Unlock className="w-5 h-5 text-green-600" />
                                            ) : (
                                                <Ban className="w-5 h-5 text-red-600" />
                                            )}
                                        </div>
                                        <div className="ml-4">
                                            <h3 className={`text-lg font-semibold ${
                                                isBlocked ? 'text-green-800' : 'text-red-800'
                                            }`}>
                                                {isBlocked ? 'Unblock User' : 'Block User'}
                                            </h3>
                                            <p className="text-sm text-gray-600">
                                                {selectedUser.full_name} ({selectedUser.mobile_number})
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Content */}
                        <div className="px-6 py-4">
                            {(() => {
                                const isBlocked = !selectedUser.is_active || selectedUser.status_display === 'Inactive';
                                return (
                                    <>
                                        <div className={`p-4 rounded-lg border ${
                                            isBlocked 
                                                ? 'bg-green-50 border-green-200' 
                                                : 'bg-red-50 border-red-200'
                                        }`}>
                                            <div className="flex">
                                                <div className="flex-shrink-0">
                                                    {isBlocked ? (
                                                        <Unlock className="w-5 h-5 text-green-400" />
                                                    ) : (
                                                        <Ban className="w-5 h-5 text-red-400" />
                                                    )}
                                                </div>
                                                <div className="ml-3">
                                                    <h4 className={`text-sm font-medium ${
                                                        isBlocked ? 'text-green-800' : 'text-red-800'
                                                    }`}>
                                                        {isBlocked ? 'Unblock Confirmation' : 'Block Confirmation'}
                                                    </h4>
                                                    <p className={`mt-1 text-sm ${
                                                        isBlocked ? 'text-green-700' : 'text-red-700'
                                                    }`}>
                                                        {isBlocked 
                                                            ? 'This user will regain access to the system and be able to log in again.' 
                                                            : 'This user will lose access to the system and will not be able to log in.'
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* User Details */}
                                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                            <h5 className="text-sm font-medium text-gray-700 mb-2">User Details:</h5>
                                            <div className="space-y-1 text-sm text-gray-600">
                                                <div><span className="font-medium">Name:</span> {selectedUser.full_name}</div>
                                                <div><span className="font-medium">Mobile:</span> {selectedUser.mobile_number}</div>
                                                <div><span className="font-medium">Status:</span> 
                                                    <span className={`ml-1 px-2 py-1 text-xs rounded-full ${
                                                        isBlocked 
                                                            ? 'bg-red-100 text-red-800' 
                                                            : 'bg-green-100 text-green-800'
                                                    }`}>
                                                        {isBlocked ? 'Currently Blocked' : 'Currently Active'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        {/* Actions */}
                        <div className="px-6 py-4 bg-gray-50 rounded-b-xl">
                            {(() => {
                                const isBlocked = !selectedUser.is_active || selectedUser.status_display === 'Inactive';
                                return (
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleBlockSubmit}
                                            className={`flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                                                isBlocked 
                                                    ? 'bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500' 
                                                    : 'bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500'
                                            }`}
                                        >
                                            {isBlocked ? (
                                                <Unlock className="w-4 h-4 mr-2" />
                                            ) : (
                                                <Ban className="w-4 h-4 mr-2" />
                                            )}
                                            {isBlocked ? 'Unblock User' : 'Block User'}
                                        </button>
                                        <button
                                            onClick={() => setShowBlockModal(false)}
                                            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Change Login Type Modal */}
            {showLoginTypeModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Change Login Type - {selectedUser.full_name}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Current Login Type</label>
                                <div className="px-3 py-2 bg-gray-100 rounded-md text-sm">
                                    {selectedUser.login_type || 'Unknown'}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">New Login Type</label>
                                <select
                                    value={loginTypeForm.new_login_type}
                                    onChange={(e) => setLoginTypeForm(prev => ({ 
                                        ...prev, 
                                        new_login_type: e.target.value as 'mpin' | 'password' 
                                    }))}
                                    className="w-full px-3 py-2 border rounded-md"
                                >
                                    <option value="mpin">MPIN</option>
                                    <option value="password">Password</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    New {loginTypeForm.new_login_type === 'mpin' ? 'MPIN' : 'Password'}
                                </label>
                                <input
                                    type={loginTypeForm.new_login_type === 'mpin' ? 'text' : 'password'}
                                    value={loginTypeForm.new_credential}
                                    onChange={(e) => setLoginTypeForm(prev => ({ ...prev, new_credential: e.target.value }))}
                                    placeholder={`Enter new ${loginTypeForm.new_login_type === 'mpin' ? 'MPIN' : 'password'}`}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleLoginTypeSubmit}
                                className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700"
                            >
                                Change Login Type
                            </button>
                            <button
                                onClick={() => setShowLoginTypeModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// List View Component
const ListView: React.FC<{ 
    users: User[]; 
    joinedSort: 'newest' | 'oldest';
    onToggleJoinedSort: () => void;
    formatDate: (date: string) => string;
    onEditUser: (user: User) => void;
    onPointsOperation: (user: User) => void;
    onAppAssignment: (user: User) => void;
    onBlockUser: (user: User) => void;
    onChangeLoginType: (user: User) => void;
}> = ({ users, joinedSort, onToggleJoinedSort, formatDate, onEditUser, onPointsOperation, onAppAssignment, onBlockUser, onChangeLoginType }) => (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button
                                type="button"
                                onClick={onToggleJoinedSort}
                                className="inline-flex items-center gap-1 hover:text-gray-800 focus:outline-none"
                                title={joinedSort === 'newest' ? 'Newest joined first' : 'Oldest joined first'}
                            >
                                Joined {joinedSort === 'newest' ? '↓' : '↑'}
                            </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <span className="text-primary font-semibold text-sm">
                                                {(user.first_name || '').charAt(0)}{(user.last_name || '').charAt(0) || 'U'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                                        <div className="text-sm text-gray-500">ID: {user.id}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{user.mobile_number}</div>
                                {user.email && <div className="text-sm text-gray-500">{user.email}</div>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-blue-800">
                                        {user.is_staff ? 'Staff' : 'User'}
                                </span>
                                    {(!user.is_active || user.status_display === 'Inactive') && (
                                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                            <Ban className="w-3 h-3 mr-1" />
                                            Blocked
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {user.points_formatted}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    user.is_active 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                }`}>
                                    {user.status_display}
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    user.login_type === 'mpin' 
                                        ? 'bg-orange-100 text-blue-800' 
                                        : user.login_type === 'password'
                                        ? 'bg-purple-100 text-purple-800'
                                        : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {user.login_type || 'Unknown'}
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(user.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={() => onEditUser(user)}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-orange-50 rounded-md hover:bg-orange-100 transition-colors"
                                        title="Edit User"
                                    >
                                        <Edit className="w-3 h-3 mr-1" />
                                        Edit
                                    </button>
                                    <button 
                                        onClick={() => onPointsOperation(user)}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-600 bg-yellow-50 rounded-md hover:bg-yellow-100 transition-colors"
                                        title="Manage Points"
                                    >
                                        <Coins className="w-3 h-3 mr-1" />
                                        Points
                                    </button>
                                    {/* <button 
                                        onClick={() => onAppAssignment(user)}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                                        title="Assign App"
                                    >
                                        <Smartphone className="w-3 h-3 mr-1" />
                                        App
                                    </button> */}
                                    <button 
                                        onClick={() => onBlockUser(user)}
                                        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                                            !user.is_active || user.status_display === 'Inactive'
                                                ? "text-green-700 bg-green-50 hover:bg-green-100" 
                                                : "text-red-700 bg-red-50 hover:bg-red-100"
                                        }`}
                                        title={!user.is_active || user.status_display === 'Inactive' ? "Unblock User" : "Block User"}
                                    >
                                        {!user.is_active || user.status_display === 'Inactive' ? <Unlock className="w-3 h-3 mr-1" /> : <Ban className="w-3 h-3 mr-1" />}
                                        {!user.is_active || user.status_display === 'Inactive' ? 'Unblock' : 'Block'}
                                    </button>
                                    <button 
                                        onClick={() => onChangeLoginType(user)}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors"
                                        title="Change Login Type"
                                    >
                                        <Key className="w-3 h-3 mr-1" />
                                        Login
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

// Grid View Component
const GridView: React.FC<{ 
    users: User[]; 
    formatDate: (date: string) => string;
    onEditUser: (user: User) => void;
    onPointsOperation: (user: User) => void;
    onAppAssignment: (user: User) => void;
    onBlockUser: (user: User) => void;
    onChangeLoginType: (user: User) => void;
}> = ({ users, formatDate, onEditUser, onPointsOperation, onAppAssignment, onBlockUser, onChangeLoginType }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {users.map((user) => (
            <div key={user.id} className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                            <span className="text-primary font-semibold text-lg">
                                {(user.first_name || '').charAt(0)}{(user.last_name || '').charAt(0) || 'U'}
                            </span>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-900">{user.full_name}</h3>
                            <p className="text-xs text-gray-500">ID: {user.id}</p>
                        </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600">
                        <MoreHorizontal className="w-4 h-5" />
                    </button>
                </div>
                
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Mobile:</span>
                        <span className="text-sm text-gray-900">{user.mobile_number}</span>
                    </div>
                    
                    {user.email && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Email:</span>
                            <span className="text-sm text-gray-900 truncate">{user.email}</span>
                        </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Role:</span>
                        <div className="flex items-center space-x-1">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-blue-800">
                                {user.is_staff ? 'Staff' : 'User'}
                        </span>
                            {(!user.is_active || user.status_display === 'Inactive') && (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                    <Ban className="w-3 h-3 mr-1" />
                                    Blocked
                                </span>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Points:</span>
                        <span className="text-sm font-medium text-gray-900">{user.points_formatted}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Status:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                        }`}>
                            {user.status_display}
                        </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Login Type:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.login_type === 'mpin' 
                                ? 'bg-orange-100 text-blue-800' 
                                : user.login_type === 'password'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}>
                            {user.login_type || 'Unknown'}
                        </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Joined:</span>
                        <span className="text-xs text-gray-500">{formatDate(user.created_at)}</span>
                    </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-2">
                        <button 
                            onClick={() => onEditUser(user)}
                            className="inline-flex items-center justify-center bg-blue-600 text-white py-2 px-3 rounded-lg text-xs hover:bg-blue-700 transition-colors"
                        >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                        </button>
                        <button 
                            onClick={() => onPointsOperation(user)}
                            className="inline-flex items-center justify-center bg-yellow-600 text-white py-2 px-3 rounded-lg text-xs hover:bg-yellow-700 transition-colors"
                        >
                            <Coins className="w-3 h-3 mr-1" />
                            Points
                        </button>
                        {/* <button 
                            onClick={() => onAppAssignment(user)}
                            className="inline-flex items-center justify-center bg-indigo-600 text-white py-2 px-3 rounded-lg text-xs hover:bg-indigo-700 transition-colors"
                        >
                            <Smartphone className="w-3 h-3 mr-1" />
                            App
                        </button> */}
                        <button 
                            onClick={() => onBlockUser(user)}
                            className={`inline-flex items-center justify-center py-2 px-3 rounded-lg text-xs transition-colors ${
                                !user.is_active || user.status_display === 'Inactive'
                                    ? "bg-green-600 text-white hover:bg-green-700" 
                                    : "bg-red-600 text-white hover:bg-red-700"
                            }`}
                        >
                            {!user.is_active || user.status_display === 'Inactive' ? <Unlock className="w-3 h-3 mr-1" /> : <Ban className="w-3 h-3 mr-1" />}
                            {!user.is_active || user.status_display === 'Inactive' ? 'Unblock' : 'Block'}
                        </button>
                        <button 
                            onClick={() => onChangeLoginType(user)}
                            className="inline-flex items-center justify-center bg-purple-600 text-white py-2 px-3 rounded-lg text-xs hover:bg-purple-700 transition-colors col-span-2"
                        >
                            <Key className="w-3 h-3 mr-1" />
                            Change Login Type
                        </button>
                    </div>
                </div>
            </div>
        ))}
    </div>
);

export default Users;
